import React, { useState, useEffect, useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../../providers/AuthContext";
import InputField from "../InputField";
import Modal from "../Modal";
import ConfirmationModal from "../ConfirmationModal";
import {
  getScoFormValues,
  addScoListValue,
  hideScoListValue,
  correctScoListValue,
} from "../../api/Tenants";
import { getUsersByTenant } from "../../api/Users";
import { offeredListValues, removedListValues } from "../user/listValues";

const FIELDS = [
  { field: "Office", userField: "officeLocation" },
  { field: "Company", userField: "company" },
  { field: "Department", userField: "department" },
];

/** The same filter the onboarding form applies (#352): rooms and shared mailboxes do not count. */
const isUnlicensedAccount = (user) =>
  !!user?.licenses && Object.keys(user.licenses).length === 0;

/**
 * Administers the Office, Company and Department values a tenant's onboarding form offers
 * (#357): those saved for the customer and those its users already carry. A removed value
 * stays out of the form even while users still carry it; the users keep their value until it
 * is corrected in their directory.
 */
const ScoListValuesSection = ({ tenantPublicId }) => {
  const { getAccessToken } = useAuth();
  const [listValues, setListValues] = useState([]);
  const [users, setUsers] = useState([]);
  // { field, value, text }: value is the spelling being corrected, "" when adding.
  const [editor, setEditor] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadValues = useCallback(async () => {
    try {
      const accessToken = await getAccessToken();
      const values = await getScoFormValues(tenantPublicId, accessToken);

      setListValues(values?.listValues || []);
    } catch (error) {
      console.error("Failed to fetch SCO list values:", error?.message ?? error);
    }
  }, [tenantPublicId, getAccessToken]);

  const loadUsers = useCallback(async () => {
    try {
      const accessToken = await getAccessToken();

      setUsers((await getUsersByTenant(tenantPublicId, accessToken)) || []);
    } catch (error) {
      console.error("Failed to fetch users:", error?.message ?? error);
    }
  }, [tenantPublicId, getAccessToken]);

  useEffect(() => {
    if (!tenantPublicId) return;

    loadValues();
    loadUsers();
  }, [tenantPublicId, loadValues, loadUsers]);

  const licensedUsers = useMemo(
    () => users.filter((tenantUser) => !isUnlicensedAccount(tenantUser)),
    [users]
  );

  const change = async (apiCall, payload, successMessage) => {
    setIsSaving(true);

    try {
      const accessToken = await getAccessToken();

      await apiCall({ tenantPublicId, ...payload }, accessToken);
      toast.success(successMessage);
      await loadValues();
      return true;
    } catch (error) {
      console.error("Failed to change an SCO list value:", error?.message ?? error);
      toast.error(error.message || "Failed to save the value");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (isSaving || !editor) return;

    const text = editor.text.trim();

    if (!text) {
      toast.error("The value cannot be empty.");
      return;
    }

    const saved = editor.value
      ? await change(
          correctScoListValue,
          { field: editor.field, value: editor.value, newValue: text },
          "Value corrected"
        )
      : await change(addScoListValue, { field: editor.field, value: text }, "Value added");

    if (saved) setEditor(null);
  };

  const handleRemove = async () => {
    const target = removing;

    setRemoving(null);

    if (target) await change(hideScoListValue, target, "Value removed from the form");
  };

  return (
    <div className="flex flex-col border-t">
      <div className="flex flex-row items-center py-4">
        <h2>SCO list values</h2>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        The Office, Company and Department values the customer's onboarding form offers: those
        saved for the customer and those its users already have. A removed value stays out of
        the form even while users still have it — the users keep their value until it is
        corrected in their directory.
      </p>

      {FIELDS.map(({ field, userField }) => {
        const offered = offeredListValues(field, listValues, licensedUsers, userField);
        const removed = removedListValues(field, listValues);

        return (
          <div key={field} className="flex flex-col mb-6">
            <div className="flex flex-row items-center mb-2">
              <h3 className="font-bold">{field}</h3>
              <button
                className="btn btn-primary-small ml-auto"
                onClick={() => setEditor({ field, value: "", text: "" })}
              >
                Add value
              </button>
            </div>

            {offered.length === 0 ? (
              <p className="text-sm text-gray-600">No values.</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {offered.map((value) => (
                    <tr key={value} className="border-b">
                      <td className="py-2 pr-4">{value}</td>
                      <td className="py-2 text-right whitespace-nowrap">
                        <button
                          className="text-primary-400 hover:underline mr-4"
                          onClick={() => setEditor({ field, value, text: value })}
                        >
                          Correct
                        </button>
                        <button
                          className="text-error-600 hover:underline"
                          onClick={() => setRemoving({ field, value })}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {removed.length > 0 && (
              <div className="text-sm text-gray-600 mt-2 flex flex-wrap gap-x-4 gap-y-1">
                <span>Removed:</span>
                {removed.map((value) => (
                  <span key={value}>
                    {value}{" "}
                    <button
                      className="text-primary-400 hover:underline"
                      disabled={isSaving}
                      onClick={() =>
                        change(addScoListValue, { field, value }, "Value offered again")
                      }
                    >
                      Offer again
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}

      <Modal
        isOpen={!!editor}
        setIsOpen={(isOpen) => {
          if (!isOpen) setEditor(null);
        }}
      >
        <h2 className="text-xl font-semibold mb-4">
          {editor?.value ? `Correct ${editor.field.toLowerCase()}` : `Add ${editor?.field.toLowerCase() || "value"}`}
        </h2>
        <div className="flex flex-col gap-4">
          {editor?.value && (
            <p className="text-sm text-gray-600">
              The form offers the corrected spelling instead of "{editor.value}". Users who have
              the old spelling keep it until it is corrected in their directory.
            </p>
          )}
          <InputField
            label={editor?.field || "Value"}
            value={editor?.text || ""}
            onChange={(e) => setEditor({ ...editor, text: e.target.value })}
            placeholder="E.g. Munka Ljungby"
            type="text"
          />
          <div className="flex gap-4 justify-end">
            <button
              className="btn btn-outline-primary-small"
              onClick={() => setEditor(null)}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button className="btn btn-primary-small" onClick={handleSave} disabled={isSaving}>
              Save
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmationModal
        isOpen={!!removing}
        title={`Remove "${removing?.value || ""}" from the onboarding form?`}
        onClose={() => setRemoving(null)}
        onConfirm={handleRemove}
      />
    </div>
  );
};

export default ScoListValuesSection;
